<template>
  <!-- 请假申请：请假单提交与审批流转。请假必须填写原因，时长可选整天/多天/某一天上午/某一天下午 -->
  <div class="leave-apply-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="leaveApplyList">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="单号/原因/申请人" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.leaveTypeId" placeholder="全部" clearable>
            <template #prefix>请假类型</template>
            <el-option v-for="t in typeList" :key="t.userCode" :label="t.dictName" :value="t.userCode"/>
          </el-select>
          <el-select v-model="searchInfo.leaveDurationType" placeholder="全部" clearable>
            <template #prefix>请假时长</template>
            <el-option v-for="d in DURATION_TYPES" :key="d.value" :label="d.label" :value="d.value"/>
          </el-select>
          <el-select v-model="searchInfo.leaveStatus" placeholder="全部" clearable>
            <template #prefix>审批状态</template>
            <el-option v-for="s in STATUS_OPTIONS" :key="s.value" :label="s.label" :value="s.value"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新增请假单</page-button>
        </template>
      </search-condition>
      <list-table>
        <!-- KPI -->
        <div class="stat-grid">
          <div class="stat-card">
            <div class="label">请假单数</div>
            <div class="value">{{ stat.applyCount || 0 }}<span class="unit">单</span></div>
            <div class="sub">不含草稿</div>
          </div>
          <div class="stat-card">
            <div class="label">请假天数</div>
            <div class="value">{{ days(stat.totalDays) }}<span class="unit">天</span></div>
          </div>
          <div class="stat-card">
            <div class="label">审批中</div>
            <div class="value" style="color:#e6a23c">{{ stat.approvingCount || 0 }}<span class="unit">单</span></div>
          </div>
          <div class="stat-card">
            <div class="label">已通过</div>
            <div class="value" style="color:#67c23a">{{ stat.passedCount || 0 }}<span class="unit">单</span></div>
          </div>
        </div>
        <!-- 口径说明：草稿不算发生额，但列表仍要展示，否则申请人没法继续改 -->
        <div class="tip-bar">
          口径说明：KPI 与三张分布图只统计「已提交」的请假单，草稿尚未提交、不计入发生额；
          列表仍会展示草稿，方便继续编辑与提交。
        </div>
        <!-- 三张分布图 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">请假类型分布（按天数）</div>
            <div ref="typeChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">各部门请假天数</div>
            <div ref="departChart" class="chart-div"></div>
          </div>
        </div>
        <div class="chart-card">
          <div class="section-title">月度请假天数（按开始日期归月）</div>
          <div ref="monthChart" class="chart-div"></div>
        </div>

        <!-- 列表 -->
        <list-table v-loading="loading" background-color="#FFF" style="height: auto">
          <yq-table :data="dataList" :empty-text="'暂无数据'" height="auto">
            <yq-table-column label="请假单号" prop="leaveNo" width="130rem">
              <template v-slot="{row}"><span class="num muted">{{ row.leaveNo }}</span></template>
            </yq-table-column>
            <yq-table-column label="申请人" prop="applicantName" width="90rem">
              <template v-slot="{row}"><span class="cell-title">{{ row.applicantName || '—' }}</span></template>
            </yq-table-column>
            <yq-table-column label="所属部门" prop="departTitle" width="150rem" :show-overflow-tooltip="true"/>
            <yq-table-column label="请假类型" prop="leaveTypeId" align="center" width="100rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="typeTagType(row.leaveTypeId)">{{ typeName(row.leaveTypeId) }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="请假时长" prop="leaveDurationType" align="center" width="110rem">
              <template v-slot="{row}">
                <el-tag size="small" type="info">{{ durationTypeName(row.leaveDurationType) }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="起止时间" prop="dateRange" width="230rem">
              <template v-slot="{row}">
                <span class="num">{{ dateOnly(row.startDate) }} {{ timeTypeName(row.startTimeType) }}</span>
                <span class="num muted"> ~ </span>
                <span class="num">{{ dateOnly(row.endDate) }} {{ timeTypeName(row.endTimeType) }}</span>
              </template>
            </yq-table-column>
            <yq-table-column label="请假天数" prop="leaveDuration" align="right" width="90rem">
              <template v-slot="{row}"><span class="num bold">{{ days(row.leaveDuration) }}</span></template>
            </yq-table-column>
            <yq-table-column label="请假原因" prop="leaveReason" min-width="180rem" :show-overflow-tooltip="true"/>
            <yq-table-column label="申请状态" prop="leaveStatus" align="center" width="95rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="statusTagType(row.leaveStatus)">{{ statusName(row.leaveStatus) }}</el-tag>
              </template>
            </yq-table-column>

            <!-- 审批状态：由工作流回写，与申请状态是两回事（见 ProcessFlowConstant.ApprovalStatus） -->
            <yq-table-column label="审批状态" prop="approvalStatus" align="center" width="95rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="approvalTagType(row.approvalStatus)">
                  {{ approvalName(row.approvalStatus) }}
                </el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="操作" prop="options" align="center" fixed="right" width="250" :show-overflow-tooltip="false">
              <template v-slot="{row}">
                <!--
                  驳回/中止/撤回后申请状态都会回到草稿，与「从没提交过的新草稿」
                  在申请状态上无从区分。按钮文案按审批状态区分：前者该「重新提交」，
                  后者才是「编辑」—— 否则用户看不出这张单子已经被打回过。
                -->
                <table-button v-if="isResubmit(row)" @click="openDialog(row)">重新提交</table-button>
                <table-button v-else-if="isDraft(row.leaveStatus)" @click="openDialog(row)">编辑</table-button>
                <table-button v-if="row.leaveStatus === STATUS.APPROVING" @click="withdrawRow(row)">撤回</table-button>
                <table-button v-if="isDraft(row.leaveStatus)" @click="deleteRow(row)">删除</table-button>
                <!-- 审批走过流程的单据才能查看流转记录；没走过的不显示入口 -->
                <table-button v-if="row.approvalStatus" @click="openFlowLog(row)">查看审批</table-button>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>
        <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                   :total-count="searchInfo.totalCount" @onLoad="queryList"/>
      </list-table>
    </list-page>

    <!-- 新增/编辑请假单 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑请假单' : '新增请假单'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="请假类型" :required="true" name="leaveTypeId" error-message="请选择请假类型">
          <el-select v-model="dialogData.leaveTypeId" placeholder="请选择" style="width: 100%">
            <el-option v-for="t in typeList" :key="t.userCode" :label="t.dictName" :value="t.userCode"/>
          </el-select>
        </form-item>
        <form-item label="所属部门">
          <!-- 默认带出当前登录人的主部门，允许改（代他人请假的情况） -->
          <depart-select v-model="dialogData.departCode" :data="departTree" placeholder="请选择部门"/>
        </form-item>
        <!-- 开始时间：日期 + 上午/下午。两者都必填，天数由系统推导 -->
        <form-item label="开始时间" :required="true" name="startDate" error-message="请选择开始日期和时段">
          <div class="datetime-row">
            <el-date-picker v-model="dialogData.startDate" type="date" value-format="YYYY-MM-DD"
                            placeholder="开始日期" :disabled-date="disablePast" @change="onTimeChange"/>
            <el-select v-model="dialogData.startTimeType" placeholder="时段" @change="onTimeChange">
              <el-option v-for="t in TIME_TYPES" :key="t.value" :label="t.label" :value="t.value"/>
            </el-select>
          </div>
        </form-item>
        <form-item label="结束时间" :required="true" name="endDate" error-message="请选择结束日期和时段">
          <div class="datetime-row">
            <el-date-picker v-model="dialogData.endDate" type="date" value-format="YYYY-MM-DD"
                            placeholder="结束日期" :disabled-date="disablePast" @change="onTimeChange"/>
            <el-select v-model="dialogData.endTimeType" placeholder="时段" @change="onTimeChange">
              <el-option v-for="t in TIME_TYPES" :key="t.value" :label="t.label" :value="t.value"/>
            </el-select>
          </div>
        </form-item>
        <!-- 天数与时长类型都是推导值，不让申请人手填 -->
        <form-item label="请假时长" :show-block="true">
          <div class="derive-tip">
            <template v-if="derivedDays !== null">
              <span class="derive-value">{{ derivedDays }} 天</span>
              <span class="derive-type">{{ derivedTypeName }}</span>
            </template>
            <span v-else class="derive-placeholder">填完起止时间后自动计算</span>
          </div>
        </form-item>
        <form-item label="请假原因" :required="true" name="leaveReason" error-message="请填写请假原因" :show-block="true">
          <el-input v-model="dialogData.leaveReason" type="textarea" :rows="3" placeholder="请假必须写明原因"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="选填"/>
        </form-item>
        <!-- 附件：多张图片，存逗号分隔的 URL 串；审批时会在单据详情里一并展示 -->
        <form-item label="附件" :show-block="true">
          <image-upload v-model="dialogData.attachUrl" :limit="6" folder="leave"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" @click="saveData">保存草稿</page-button>
        <page-button type="write" el-type="primary" @click="submitData">提交</page-button>
      </template>
    </el-dialog>

    <!-- 审批流转记录：让申请人看到自己单据的审批过程（谁批的、什么意见、什么时候） -->
    <el-drawer v-model="showFlowLog" title="审批流转记录" size="60%" :destroy-on-close="true">
      <div v-loading="flowLogLoading" class="flow-log-wrap">
        <flow-log-panel :detail="flowLogDetail"
                        :empty-text="flowLogEmptyText"/>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import DepartSelect from "@/components/h5/data/DepartSelect.vue";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";
import FlowLogPanel from "@/views/h5/common/approval/FlowLogPanel.vue";
import leaveApplyApi from "@/api/dz-crm/leave/leaveApplyApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";
import YqTable from "@/components/h5/table/YqTable.vue";

/** 请假状态，与后端 LeaveApplyConstant.Status 对齐 */
const STATUS = {DRAFT: 0, APPROVING: 100, PASSED: 200, REJECTED: 300};
const STATUS_OPTIONS = [
  {value: STATUS.DRAFT, label: "草稿"},
  {value: STATUS.APPROVING, label: "审批中"},
  {value: STATUS.PASSED, label: "已通过"},
  {value: STATUS.REJECTED, label: "已驳回"},
];
const STATUS_TAG = {0: "info", 100: "warning", 200: "success", 300: "danger"};

/** 请假时段，与后端 LeaveApplyConstant.TimeType 对齐 */
const TIME_TYPES = [
  {value: 1, label: "上午"},
  {value: 2, label: "下午"},
];
/** 时长类型是推导结果，仅用于展示与筛选 */
const DURATION_TYPES = [
  {value: 100, label: "整天"},
  {value: 200, label: "多天"},
  {value: 300, label: "某一天上午"},
  {value: 400, label: "某一天下午"},
];
/** 时段在一天内的序号：上午 0、下午 1 */
const slotOf = (t) => (t === 2 ? 1 : 0);
/**
 * 推导请假天数（天），与后端 LeaveApplyConstant.halfDays 同一公式：
 *   半天数 = (结束日期 - 开始日期) × 2 + (结束时段 - 开始时段) + 1
 * 返回 null 表示起止时间还没填全或非法，此时不展示结果。
 */
function deriveDays(startDate, startTimeType, endDate, endTimeType) {
  if (!startDate || !endDate || !startTimeType || !endTimeType) {
    return null;
  }
  const s = new Date(String(startDate).slice(0, 10) + "T00:00:00");
  const e = new Date(String(endDate).slice(0, 10) + "T00:00:00");
  if (isNaN(s.getTime()) || isNaN(e.getTime())) {
    return null;
  }
  const dayDiff = Math.round((e - s) / 86400000);
  const halfDays = dayDiff * 2 + slotOf(endTimeType) - slotOf(startTimeType) + 1;
  return halfDays > 0 ? halfDays * 0.5 : null;
}
/** 由起止时间推导时长类型，与后端 LeaveApplyConstant.durationTypeOf 同一口径 */
function deriveDurationType(startDate, startTimeType, endDate, endTimeType) {
  const s = new Date(String(startDate).slice(0, 10) + "T00:00:00");
  const e = new Date(String(endDate).slice(0, 10) + "T00:00:00");
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || s.getTime() !== e.getTime()) {
    return 200; // 跨天一律「多天」
  }
  const startMorning = slotOf(startTimeType) === 0;
  const endMorning = slotOf(endTimeType) === 0;
  if (startMorning && endMorning) {
    return 300;
  }
  if (!startMorning && !endMorning) {
    return 400;
  }
  return 100;
}
/** 假别标签色，按字典顺序循环取，字典可增删所以不写死映射 */
const TYPE_COLORS = ["", "warning", "danger", "success", "info"];
/** 审批状态（工作流维度），与后端 ProcessFlowConstant.ApprovalStatus 对齐 */
const APPROVAL_OPTIONS = [
  {value: 0, label: "未开始"},
  {value: 100, label: "审批中"},
  {value: 200, label: "已中止"},
  {value: 300, label: "已完成"},
  {value: 400, label: "已撤回"},
  {value: 500, label: "已驳回"},
];



export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "LeaveApplyList",
  components: {
    YqTable,
    ListPage, ListTable, SearchCondition, FormPage, FormItems, FormItem,
    PageButton, TableButton, DepartSelect, ImageUpload, FlowLogPanel,
  },
  data() {
    return {
      STATUS,
      STATUS_OPTIONS,
      TIME_TYPES,
      DURATION_TYPES,
      loading: false,
      // 审批流转记录抽屉
      showFlowLog: false,
      flowLogLoading: false,
      flowLogDetail: null,
      flowLogEmptyText: "该单据尚未发起审批",
      searchInfo: {
        pageNumber: 1, pageSize: 20, searchKey: "",
        leaveTypeId: "", leaveDurationType: "", leaveStatus: "",
      },
      dataList: [],
      stat: {},
      userInfo: {},
      typeList: [],
      departTree: [],
      showDialog: false,
      dialogData: {},
      typeChart: null,
      departChart: null,
      monthChart: null,
    };
  },
  computed: {
    /** 推导出的请假天数，null 表示起止时间未填全或非法 */
    derivedDays() {
      return deriveDays(this.dialogData.startDate, this.dialogData.startTimeType,
          this.dialogData.endDate, this.dialogData.endTimeType);
    },
    /** 推导出的时长类型名称，与天数配套展示 */
    derivedTypeName() {
      if (this.derivedDays === null) {
        return "";
      }
      const type = deriveDurationType(this.dialogData.startDate, this.dialogData.startTimeType,
          this.dialogData.endDate, this.dialogData.endTimeType);
      return this.durationTypeName(type);
    },
  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  async mounted() {
    // 当前登录人信息，用于新增时预填所属部门
    this.userInfo = await Util.getUserInfo();
    // 请假类型是一致性字典（type = LEAVE_TYPE），取值 userCode / 显示 dictName
    commonApi.queryUniqueDictionaryList("LEAVE_TYPE").then(list => this.typeList = list || []);
    commonApi.queryDepartList().then(list => this.departTree = list || []);
    this.resetSearch();
    this.loadStat();
  },
  unmounted() {
    [this.typeChart, this.departChart, this.monthChart].forEach(c => c && c.dispose());
  },
  methods: {
    /**
     * 查看该单据的审批流转记录。
     *
     * <p>按 businessType + 单据 id 反查流程实例 —— 业务页手里只有自己的单据 id，
     * 没有流程实例 id。查不到说明这张单子没走过审批（草稿或工作流未启用），
     * 给出明确提示而不是弹一个空抽屉。
     */
    async openFlowLog(row) {
      this.showFlowLog = true;
      this.flowLogLoading = true;
      this.flowLogDetail = null;
      this.flowLogEmptyText = "该单据尚未发起审批";
      try {
        // 走本页自己的接口，业务类型由服务端写死 —— 不要再调审批中心的 checkApi
        const detail = await leaveApplyApi.queryFlowInstance(row.id);
        this.flowLogDetail = detail || null;
        if (!detail) {
          this.flowLogEmptyText = "该单据尚未发起审批";
        }
      } catch (e) {
        this.flowLogEmptyText = "审批记录加载失败，请稍后重试";
        this.$message.warning("审批记录加载失败");
      } finally {
        this.flowLogLoading = false;
      }
    },
    /**
     * 是否是「被打回后待重新提交」的草稿。
     *
     * <p>驳回/中止/撤回都会让申请状态回到草稿，光看申请状态与新建草稿无异。
     * 需要靠审批状态才能看出它经历过一次审批，按钮因此显示「重新提交」。
     */
    isResubmit(row) {
      const s = row.approvalStatus;
      return s === 500 || s === 200 || s === 400;
    },

    /**
     * 审批状态名。
     *
     * <p>与申请状态是两回事：申请状态回答「这张单子走到哪一步」，
     * 审批状态回答「它的审批走到哪一步」。取值口径见后端 ApprovalStatus。
     */
    approvalName(status) {
      const hit = APPROVAL_OPTIONS.find(o => o.value === status);
      return hit ? hit.label : "未开始";
    },
    /** 审批状态标签色：进行中橙、完成绿、中止红、未开始灰 */
    approvalTagType(status) {
      if (status === 100) {
        return "warning";
      }
      if (status === 300) {
        return "success";
      }
      if (status === 500) {
        // 已驳回用橙色：它需要申请人去改，是个待办信号
        return "warning";
      }
      if (status === 200) {
        return "danger";
      }
      // 未开始与已撤回都用灰：都不是「出了事」，只是「还没在跑」
      return "info";
    },
    async queryList() {
      this.loading = true;
      try {
        const {records, totalCount} = await leaveApplyApi.queryList(this.cleanParams());
        this.dataList = records || [];
        this.searchInfo.totalCount = totalCount;
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
      this.loadStat();
    },
    /** 空值不提交，避免空串绑到后端 Integer 报错 */
    cleanParams() {
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      return params;
    },
    async loadStat() {
      const params = this.cleanParams();
      this.stat = await leaveApplyApi.stat(params) || {};
      this.$nextTick(() => this.renderCharts());
    },
    renderCharts() {
      const typeRows = this.stat.typeRows || [];
      const departRows = this.stat.departRows || [];
      const monthRows = this.stat.monthRows || [];

      this.typeChart = this.renderPie(this.typeChart, this.$refs.typeChart,
          typeRows.map(r => ({name: this.typeName(r.dimKey), value: Number(r.totalDays || 0)})));
      // 部门名后端已关联好（组织架构域数据），直接用
      this.departChart = this.renderBar(this.departChart, this.$refs.departChart,
          departRows.map(r => r.dimTitle || r.dimKey || "未分配"),
          departRows.map(r => Number(r.totalDays || 0)), "#13b8b8", true);
      this.monthChart = this.renderBar(this.monthChart, this.$refs.monthChart,
          monthRows.map(r => r.dimKey), monthRows.map(r => Number(r.totalDays || 0)), "#7a5af8", false);
    },
    renderPie(chart, dom, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 天"},
        legend: {bottom: 0, type: "scroll"},
        series: [{
          type: "pie", radius: ["45%", "68%"], center: ["50%", "45%"],
          label: {formatter: "{b}\n{c} 天"},
          data: data,
        }],
      }, true);
      return chart;
    },
    /** 横向柱图（部门排名）用 yAxis 类目，纵向柱图（月度）用 xAxis 类目 */
    renderBar(chart, dom, names, data, color, horizontal) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      const categoryAxis = {
        type: "category", data: names,
        axisTick: {alignWithLabel: true}, axisLabel: {color: "#909399", interval: 0},
      };
      const valueAxis = {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}};
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: horizontal ? valueAxis : categoryAxis,
        yAxis: horizontal ? categoryAxis : valueAxis,
        series: [{type: "bar", barWidth: "40%", itemStyle: {color: color}, data: data}],
      }, true);
      return chart;
    },
    /*
     * 匹配一律用 userCode，不是字典 id。
     * 单据里存的是 userCode（如 ANNUAL_LEAVE）—— 它是稳定的业务编码，
     * 而 id 是主键，重新导入字典就会变，存量单据立刻失去关联。
     */

    /** 类型名走字典关联 —— 后端只返回 leaveTypeId（存的是 userCode） */
    typeName(typeUserCode) {
      const t = this.typeList.find(x => x.userCode === typeUserCode);
      return t ? t.dictName : "";
    },
    /** 假别标签色按字典顺序取，字典增删都不影响 */
    typeTagType(typeUserCode) {
      const index = this.typeList.findIndex(x => x.userCode === typeUserCode);
      if (index < 0) {
        return undefined;
      }
      const color = TYPE_COLORS[index % TYPE_COLORS.length];
      return color === "" ? undefined : color;
    },
    timeTypeName(value) {
      const t = TIME_TYPES.find(x => x.value === value);
      return t ? t.label : "";
    },
    durationTypeName(value) {
      const d = DURATION_TYPES.find(x => x.value === value);
      return d ? d.label : "";
    },
    statusName(status) {
      const s = STATUS_OPTIONS.find(x => x.value === status);
      return s ? s.label : "";
    },
    statusTagType(status) {
      return STATUS_TAG[status];
    },
    isDraft(status) {
      return status === STATUS.DRAFT;
    },
    today() {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    days(value) {
      if (value === null || value === undefined) {
        return "0";
      }
      return String(Number(value));
    },
    disablePast(date) {
      // 只允许今天及以后，避免补录历史假单
      return date.getTime() < new Date().setHours(0, 0, 0, 0);
    },
    /**
     * 起止时间变化时，把结束日期默认跟随开始日期。
     * 只在结束日期还没填时跟随，已填的不要覆盖 —— 用户可能就是要请多天。
     */
    onTimeChange() {
      if (this.dialogData.startDate && !this.dialogData.endDate) {
        this.dialogData.endDate = this.dialogData.startDate;
      }
      // 结束早于开始时，把结束拉平到开始，避免出现负数天数
      if (this.dialogData.startDate && this.dialogData.endDate
          && this.derivedDays === null) {
        this.dialogData.endDate = this.dialogData.startDate;
        this.dialogData.endTimeType = this.dialogData.startTimeType;
      }
    },
    /**
     * 打开新增/编辑弹框
     *
     * ★ 新增时必须先向后端申请一个编码当 id —— 工作流拦截器要从 body 里
     *   取 biz_key_field(id) 的值去查流程实例，id 必须先于提交存在。
     *   单号 leaveNo 与 id 共用这个值，不再由后端另外生成。
     */
    async openDialog(row) {
      if (row) {
        this.dialogData = JSON.parse(JSON.stringify(row));
      } else {
        // 默认「今天上午 → 今天下午」，即整天，用户按需改
        const today = this.today();
        let id = "";
        try {
          id = await leaveApplyApi.applyCode();
        } catch (e) {
          this.$message.error("申请单号失败，请稍后重试");
          return;
        }
        this.dialogData = {
          id: id, leaveNo: id,
          departCode: this.userInfo.mainDepartCode || "",
          startDate: today, startTimeType: 1,
          endDate: today, endTimeType: 2,
        };
      }
      this.showDialog = true;
    },
    /**
     * 保存草稿。
     * ★ 必须显式传 leaveStatus：工作流拦截器要从 body 里读这个字段的值
     *   来判断是否发起流程，不传的话它读到 null 会直接放行。
     */
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) {
        return;
      }
      const payload = Object.assign({}, this.dialogData, {leaveStatus: STATUS.DRAFT});
      await leaveApplyApi.saveDraft(payload);
      this.showDialog = false;
      this.resetSearch();
    },
    /**
     * 提交：保存当前表单并直接进入审批流程。
     * 与保存草稿共用同一套校验，区别只在服务端最后把状态置为审批中。
     */
    submitData() {
      if (!this.$refs.dialogForm.validCheck()) {
        return;
      }
      // ★ leaveStatus 必须随 body 一起传：它同时是工作流的 status_trigger，
      //   拦截器从 body 读它来决定是否发起流程
      const payload = Object.assign({}, this.dialogData, {leaveStatus: STATUS.APPROVING});
      this.$confirm("提交后进入审批流程，提交期间不可编辑，确认提交？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.saveSubmit(payload))
          .then(() => {
            this.showDialog = false;
            this.resetSearch();
          })
          .catch(() => {});
    },
    withdrawRow(row) {
      this.$confirm("撤回后请假单退回草稿，需重新提交，确认撤回？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.withdrawLeave(row.id))
          .then(() => this.resetSearch())
          .catch(() => {});
    },
    deleteRow(row) {
      this.$confirm("确认删除这张请假单？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.delete(row.id))
          .then(() => this.resetSearch())
          .catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.leave-apply-list{
}
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }

      &.neg {
        color: #e5484d;
      }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}

/* 起止时间：日期占满剩余宽度，时段固定宽度 */
.datetime-row {
  display: flex;
  gap: 8px;
  width: 100%;

  :deep(.el-date-editor) {
    flex: 1;
    min-width: 0;
  }

  :deep(.el-select) {
    flex: 0 0 88px;
    width: 88px;
  }
}

/* 推导结果展示，只读 */
.derive-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  padding: 0 12px;
  background: #f5f9ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;

  .derive-value {
    font-size: 15px;
    font-weight: 600;
    color: #1677ff;
    font-variant-numeric: tabular-nums;
  }

  .derive-type {
    font-size: 12px;
    color: #606266;
  }

  .derive-placeholder {
    font-size: 12px;
    color: #a8abb2;
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.neg {
  color: #e5484d;
}

.bold {
  font-weight: 600;
  color: #303133;
}

.muted {
  color: #909399;
}

.cell-title {
  color: #303133;
  font-weight: 500;
}
</style>
